/**
 * The whole router, for the interactive pages.
 *
 * The site is real paths (`site/paths.ts`): the configurator is `/sheds/design/`, the
 * plans page is `/plans/` and the shed film is `/sheds/film/`, each its own HTML entry,
 * and every other page is a static file. What is left to route is what comes after the
 * `#` on those pages, which is a query more than a route, and still does not justify a
 * routing library.
 *
 * The hash carries a query of its own, which is how a purchase comes back from Stripe:
 * `/plans/#/purchase?checkout=cs_…`, with Stripe's own session id written in by Stripe.
 * It lives after the `#` rather than before it, because everything before the `#` is the
 * static host's business and an id in it would be in the host's access log. After it,
 * the string never leaves the browser — it is not sent with the request for the page.
 * A design carried in a link (`#/?design=…`) and a preset's slug (`#/?preset=…`) live
 * there for the same reason.
 */

import { CHECKOUT_CANCELED_HASH } from "@planforge/api/wire";
import { useEffect, useState } from "react";

/** What follows the `#`: a path (`purchase`, or empty) and its own query string. */
export interface AppLocation {
  path: string;
  params: URLSearchParams;
}

export function locationFromHash(hash: string): AppLocation {
  const [path = "", query = ""] = hash.replace(/^#\/?/, "").split("?", 2);
  return { path: path.replace(/\/+$/, ""), params: new URLSearchParams(query) };
}

// ---- the configurator ------------------------------------------------------------------

// Its own state is on the bare hash (`#/?…`): a query on any other path names nothing
// the configurator opens, so a stray `#/terms?design=…` does not replace the design.

/**
 * The configurator, on this page, opened on one of the ready-made designs
 * (`presets/catalog.ts`). From another page, `presetAddress` (`site/paths.ts`).
 *
 * A slug rather than the spec itself, so a link indexed a year ago opens the design as
 * the catalog has it now — built by today's engine — and a spec in a URL is not a thing
 * a stranger can hand-edit into a shed the page never offered.
 */
export function presetHash(slug: string): string {
  return `#/?preset=${encodeURIComponent(slug)}`;
}

/** The preset a configurator address asks for, or `undefined`. */
export function presetSlug(location: AppLocation): string | undefined {
  if (location.path !== "") {
    return undefined;
  }
  const slug = location.params.get("preset");
  return slug === null || slug === "" ? undefined : slug;
}

/**
 * The configurator, opened on a design carried in the address itself (`share/link.ts`).
 *
 * What the "Open on your phone" code points at: the shopper's own design, from their
 * own other screen. Unlike a preset it is the spec rather than a name for one, because
 * there is nowhere else for it to be — the design lives in one browser and the edge keeps
 * none (DESIGN §9).
 */
export function designHash(code: string): string {
  return `#/?design=${encodeURIComponent(code)}`;
}

/** The design code a configurator address carries, or `undefined`. */
export function designCode(location: AppLocation): string | undefined {
  if (location.path !== "") {
    return undefined;
  }
  const code = location.params.get("design");
  return code === null || code === "" ? undefined : code;
}

const CANCELED = locationFromHash(CHECKOUT_CANCELED_HASH);

/**
 * Whether this is the configurator a shopper lands on after backing out of Stripe.
 *
 * Read off the same constant the edge hands Stripe as the `cancel_url`, so the two
 * cannot come to disagree about what a cancel looks like.
 */
export function isCheckoutCanceled(location: AppLocation): boolean {
  return (
    location.path === CANCELED.path &&
    [...CANCELED.params].every(([key, value]) => location.params.get(key) === value)
  );
}

// ---- the plans page --------------------------------------------------------------------

/** The plans page's two screens: the list (`#/`) and a payment coming back. */
export type PlansScreen = "plans" | "purchase";

/** Anything but `#/purchase` is the list, so a hand-typed address still lands somewhere. */
export function plansScreen(location: AppLocation): PlansScreen {
  return location.path === "purchase" ? "purchase" : "plans";
}

/** The plans page's purchase screen, on this page. From another, `purchaseAddress`. */
export function purchaseHash(checkoutId: string): string {
  return `#/purchase?checkout=${encodeURIComponent(checkoutId)}`;
}

// ---- either page -----------------------------------------------------------------------

/** The current location, following the browser's back and forward buttons. */
export function useLocation(): AppLocation {
  const [hash, setHash] = useState(() =>
    typeof window === "undefined" ? "" : window.location.hash,
  );

  useEffect(() => {
    const onChange = (): void => setHash(window.location.hash);
    window.addEventListener("hashchange", onChange);
    // The hash can have moved between the first render and this effect running.
    onChange();
    return () => window.removeEventListener("hashchange", onChange);
  }, []);

  // Derived rather than stored, so the two can never disagree.
  return locationFromHash(hash);
}
